import React from 'react';
import ReactGA from 'react-ga4';
import { FormattedMessage, useIntl } from 'react-intl';

import PageBaseProps from 'types/PageBase';
import { ButtonType } from 'components/ui/Button';
import { ChannelEnum } from 'modules/auth/types/LoginRequest';

import { b, link } from 'utils/i18nUtils';
import { formatPhoneNumber } from 'modules/communication/utils';

import Footer from 'components/ui/PageFooter';
import { Button, Icon, Input, PageHeader } from 'components/ui';
import { PhoneNumber } from 'modules/selfServices/types/SelfService';

import LoginTerms from './LoginTerms';

interface VerifyCodeFormProps extends PageBaseProps {
  code: string;
  onCodeChange: (code: string) => void;
  onVerify: () => void;
  isLoading: boolean;
  isDisabled: boolean;
  inputError?: string | boolean;
  channel: ChannelEnum;
  contactInfo?: string;
  supportPhoneNumber?: PhoneNumber;
  confidentialityPolicyLink?: string;
  legalNotice?: string;
  hideGTC?: boolean;
  onResend: () => void;
  shouldDisplayHelper?: boolean;
  canUseGA?: boolean;
}

const VerifyCodeForm: React.FC<VerifyCodeFormProps> = ({
  onPrev,
  code,
  onCodeChange,
  onVerify,
  isLoading,
  isDisabled,
  inputError,
  channel,
  contactInfo,
  supportPhoneNumber,
  confidentialityPolicyLink,
  legalNotice,
  hideGTC,
  onResend,
  shouldDisplayHelper,
  canUseGA,
}) => {
  const intl = useIntl();

  const hasPolicy = !hideGTC && (Boolean(confidentialityPolicyLink) || Boolean(legalNotice));

  if (canUseGA && shouldDisplayHelper) {
    ReactGA.event({
      category: 'Help',
      action: 'Helper displayed',
      label: 'Issue with OTP',
    });
  }

  const sentChannelMessage = {
    id: `login.sentBy.${channel}`,
    defaultMessage: `Verification ${channel} sent to <b>{contactInfo}</b>`,
    values: {
      contactInfo,
    },
  };

  const handleSendCode = React.useCallback((event: React.MouseEvent) => {
    event.preventDefault();
    onResend();
  }, [onResend]);

  const handleInputChange = React.useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    onCodeChange(event.target.value);
  }, [onCodeChange]);

  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter' && !isDisabled) {
      onVerify();
    }
  };

  return (
    <>
      <PageHeader
        hideSteps
        onPrev={onPrev}
        title={<FormattedMessage id="login.verificationCode" defaultMessage="Verification code" />}
      />
      <div className="main-content">
        <div className="content !m-auto w-full text-center">
          <FormattedMessage {...sentChannelMessage} values={{ b, ...sentChannelMessage.values }} tagName="span" />
          <Input
            center
            className="mt-4"
            value={code}
            onKeyDown={handleKeyDown}
            onChange={handleInputChange}
            label={intl.formatMessage({ id: 'login.verificationCode', defaultMessage: 'Verification code' })}
            autoComplete="one-time-code"
            type="number"
            inputMode="numeric"
            autoCorrect="off"
            spellCheck="false"
            error={inputError}
            data-testid="code-input"
          />
          <div className="text-center">
            <FormattedMessage
              id="login.didntReceiveCode"
              defaultMessage="Resend the code via the same channel or switch to another channel available in your file.{linebreak}<link>Resend the code</link>"
              values={{
                linebreak: <br />,
                link: link({
                  className: 'font-bold',
                  onClick: handleSendCode,
                  'data-testid': 'resend-button',
                }),
              }}
              tagName="span"
            />
          </div>
          {shouldDisplayHelper && supportPhoneNumber && (
            <div
              data-testid="login-helper"
              className="flex items-center justify-center bg-card-bg rounded-modal p-5 w-full"
            >
              <Icon className="fb-icon--support-24 text-2xl kiosk:text-5xl text-default shrink-0" />
              <span className="ml-4">
                <FormattedMessage
                  id="login.helpbox"
                  defaultMessage="If you are unable to log in, we can help! <b>Go to the front desk</b> or call the <b>{helpline}</b>."
                  values={{ b, helpline: formatPhoneNumber(supportPhoneNumber) }}
                />
              </span>
            </div>
          )}
          {hasPolicy && (
            <div className="justify-center">
              <div className="w-full h-px mb-4 kiosk:mb-8 bg-border-default" />
              <LoginTerms confidentialityPolicyLink={confidentialityPolicyLink} legalNotice={legalNotice} />
            </div>
          )}
        </div>

        <Footer
          hideNextButton
          onNext={onVerify}
          loading={isLoading}
          disabled={isDisabled}
          onPrev={onPrev}
          shouldDisplayBackButton
        >
          <Button
            testId="verify"
            loading={isLoading}
            disabled={isDisabled}
            type={ButtonType.TERTIARY}
            onClick={onVerify}
            className="footer-button"
          >
            <FormattedMessage id="login.verify" defaultMessage="Verify" />
          </Button>
        </Footer>
      </div>
    </>
  );
};

export default VerifyCodeForm;
